import { Controller } from "@hotwired/stimulus";
import ajax from "@fdaciuk/ajax";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

// Store files globally to persist across controller instances
let globalSelectedFiles = [];

export default class extends Controller {
  isSubmitting = false;
  
  connect() {
    // Only disable body scroll if this is a modal/overlay
    // Not for inline forms
    if (this.element.classList.contains('modal') || this.element.classList.contains('overlay')) {
      disableBodyScroll(this.element);
    }
    this.form = this.element.querySelector("form");
    
    // Use global storage for files to preserve them through form navigation
    this.selectedFiles = globalSelectedFiles;

    // Make sure we capture ALL form submissions
    this.form.addEventListener("submit", this.onSubmit.bind(this), true);
    
    // Capture file selection and store in memory
    const fileInput = this.form.querySelector('input[type="file"][name="images[]"]');
    if (fileInput) {
      fileInput.setAttribute('data-listener-attached', 'true');
      fileInput.addEventListener('change', (e) => {
        this.selectedFiles = Array.from(e.target.files);
        globalSelectedFiles = this.selectedFiles; // Store globally
      });
    }

    setTimeout(() => {
      this.element.classList.add("active");
      // Removed auto-focus that was causing unwanted scrolling
    }, 200);

    this.navButtons = this.element.querySelectorAll("nav button");

    let steps = this.form.querySelectorAll(".step");
    this.currentStep = 0;

    this.steps = [
      {
        el: steps[0],
        render() {},
        validate() {
          let email = this.el.querySelector('input[name="u-email"]');
          let phone = this.el.querySelector('input[name="u-phone"]');

          if (!email.value && !phone.value) {
            this.el.innerHTML +=
              '<div class="member__form__error">Vi trenger en måte å kontakte deg på. Oppgi e-postadresse eller telefonnummer for å fortsette.</div>';

            return false;
          }

          return true;
        },
      },
      {
        el: steps[1],
        render() {
          let messageEl = this.el.querySelector('textarea[name="u-message"]');
          if (messageEl) {
            let messageRect = messageEl.getBoundingClientRect();
            if (messageRect.top < 0) {
              window.scrollTo({
                top: window.scrollY + messageRect.top - 200,
                behavior: "smooth",
              });
            }
          }
        },
        validate() {
          return true;
        },
      },
      {
        el: steps[2],
        form: this.form,
        render() {
          let summary = this.el.querySelector(".summary");
          let labels = {
            "u-name": "Navn",
            "u-email": "E-post",
            "u-phone": "Telefon",
            "u-message": "Henvendelse",
          };
          let formData = new FormData(this.form);
          let data = {};

          for (let pair of formData.entries()) {
            if (!labels[pair[0]]) {
              continue;
            }

            if (pair[1].length) {
              data[pair[0]] = pair[1];
            }
          }

          let html = `<button class="button button--shy" data-action="click->member#onNavClick" data-index="0"><svg class="icon normal"><use xlink:href="#arrow-return"></use></svg>Endre</button><div>`;

          for (let property in data) {
            let val =
              data[property].length > 20
                ? data[property].slice(0, 20) + "..."
                : data[property];
            html += `
              <div>
                <span>${labels[property]}</span>
                <span>${val}</span>
              </div>`;
          }

          html += `</div>`;

          this.form.querySelectorAll(".fancy-radio").forEach((el) => {
            el.classList.remove("active");
          });
          this.form
            .querySelector(".member__form__info")
            .classList.remove("active");
          this.form.querySelector(".contact-by").classList.remove("active");

          let choises = 0;
          if (data["u-phone"]) {
            this.form
              .querySelector(".fancy-radio.phone")
              .classList.add("active");
            choises++;
          }

          if (data["u-email"]) {
            this.form
              .querySelector(".fancy-radio.email")
              .classList.add("active");
            choises++;
          }

          if (choises >= 2) {
            this.form.querySelector(".contact-by").classList.add("active");
          }

          summary.innerHTML = html;
        },
        validate() {
          this.el.querySelector('button[type="submit"]').innerHTML =
            "Sender...";
          this.el.querySelector('button[type="submit"]').disabled = true;
          return true;
        },
      },
    ];
  }

  disconnect() {
    if (this.element.classList.contains('modal') || this.element.classList.contains('overlay')) {
      enableBodyScroll(this.element);
    }
  }

  close() {
    this.element.remove();
  }

  onNavClick(evt) {
    evt.preventDefault();
    let index = parseInt(evt.currentTarget.dataset.index);
    
    
    const valid = this.steps[this.currentStep].validate();
    if (!valid) {
      return;
    }
    this.goTo(index);
  }

  goTo(index) {
    if (index < 0 && index >= this.steps.length) {
      return;
    }

    this.steps[this.currentStep].el.classList.remove("active");
    this.navButtons[this.currentStep].classList.remove("active");

    this.steps[index].el.classList.add("active");
    this.navButtons[index].classList.add("active");
    this.navButtons[index].disabled = false;

    this.currentStep = index;

    this.steps[index].render();
    
    // Re-attach file input listener if we're on step with file input
    const fileInput = this.steps[index].el.querySelector('input[type="file"][name="images[]"]');
    if (fileInput && !fileInput.hasAttribute('data-listener-attached')) {
      fileInput.setAttribute('data-listener-attached', 'true');
      fileInput.addEventListener('change', (e) => {
        this.selectedFiles = Array.from(e.target.files);
        globalSelectedFiles = this.selectedFiles; // Store globally
      });
    }

    this.element.scrollTo({
      top: this.element.querySelector(".wrap").offsetTop - 104,
      behavior: "smooth",
    });
  }

  onSubmit(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    // Prevent double submission
    if (this.isSubmitting) {
      return false;
    }

    this.form
      .querySelectorAll(".member__form__error")
      .forEach((e) => e.remove());
    let valid = this.steps[this.currentStep].validate();

    if (!valid) {
      return;
    }

    if (this.currentStep + 1 < this.steps.length) {
      this.goTo(this.currentStep + 1);
      return false;
    }

    // Set flag to prevent double submission
    this.isSubmitting = true;

    // Create fresh FormData
    const ajaxFormData = new FormData();
    
    // Manually add all form fields EXCEPT file input
    const formElements = this.form.elements;
    for (let element of formElements) {
      if (element.type !== 'file' && element.name) {
        if (element.type === 'checkbox' || element.type === 'radio') {
          if (element.checked) {
            ajaxFormData.append(element.name, element.value);
          }
        } else {
          ajaxFormData.append(element.name, element.value);
        }
      }
    }
    
    // Add the action for WordPress AJAX
    ajaxFormData.append("action", "mailgun_form_submission");
    
    // Now add the files from our stored array
    // Use global files if local is empty
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.selectedFiles = globalSelectedFiles;
    }
    
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      // Add all files using the images[] array format that PHP expects
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        ajaxFormData.append('images[]', file);
      }
      
      // Also add individually named files as backup
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const fieldName = `image_${i}`;
        const file = this.selectedFiles[i];
        ajaxFormData.append(fieldName, file);
      }
      
      // Add count so PHP knows how many files to look for
      ajaxFormData.append('images_count', this.selectedFiles.length);
    } else {
      ajaxFormData.append('images_count', 0);
    }

    fetch(_ajaxurl, {
      method: "POST",
      body: ajaxFormData,
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.success) {
          // this.form.querySelector(".extra").remove();
          window.dataLayer.push({
            event: "member.submission",
          });
          this.form.querySelector("nav").remove();

          let thankYou = `
          <div class="thank-you">
            <h2>Takk for din henvendelse!</h2>
            <p>Vi vil ta kontakt så raskt som mulig.</p>
            <button class="button button--primary" data-action="click->member#close">Lukk</button>
          </div>
        `;

          this.form.querySelector(".wrap > div").innerHTML = thankYou;

          window.dispatchEvent(
            new CustomEvent("member.submission", {
              detail: {},
            })
          );
        } else {
          // Handle error
          // Silently handle error
          // Re-enable submit button and clear flag
          this.isSubmitting = false;
          this.steps[this.currentStep].el.querySelector(
            'button[type="submit"]'
          ).innerHTML = "Send";
          this.steps[this.currentStep].el.querySelector(
            'button[type="submit"]'
          ).disabled = false;
        }
      })
      .catch((error) => {
        // Silently handle error since email is likely sent anyway
        // Re-enable submit button and clear flag
        this.isSubmitting = false;
        this.steps[this.currentStep].el.querySelector(
          'button[type="submit"]'
        ).innerHTML = "Send";
        this.steps[this.currentStep].el.querySelector(
          'button[type="submit"]'
        ).disabled = false;
      });
  }
}
